import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { portalFetch } from "../../api/http";
import { contactBaseTextKeys, contactTextPrefixes } from "../../contacts/contactModel";
import { contactTextKeys } from "../../contacts/contactTextKeys";
import { ContactWorkspace } from "../../contacts/ContactWorkspace";
import { missingText } from "../../i18n/texts";
import type { ShellData } from "../../shell/shellModel";

/**
 * Contactos del Chat (/portal/chat/contactos y /portal/chat/contactos/{id}), como Contactos.razor del portal actual: las
 * claves de la pantalla y el espacio de trabajo. Una sola ruta para la lista y la ficha, así el espacio se conserva al
 * abrir un contacto o volver a la lista (en Blazor es la misma página con dos direcciones).
 */
export function ContactsPage({ shell, locale }: { shell: ShellData; locale: string }) {
  const location = useLocation();
  const [user, tenant] = shell.reportScope.split(":").map(Number) as [number, number];
  const texts = useQuery({
    queryKey: ["texts", locale, "contacts"],
    queryFn: async () => {
      const all = await portalFetch<Record<string, string>>(
        `/api/portal/texts/${locale}?prefixes=${encodeURIComponent(contactTextPrefixes.join(","))}`,
      );
      // Solo las claves que pide el portal actual: una que falte se ve marcada, igual que hoy.
      return Object.fromEntries(
        [...contactTextKeys, ...contactBaseTextKeys].map((key) => [key, all[key] ?? missingText(key)]),
      );
    },
  });
  const match = /^\/portal\/chat\/contactos(?:\/(\d+))?\/?$/i.exec(location.pathname);
  if (!texts.data || !match) return null;
  const id = match[1] !== undefined ? Number(match[1]) : null;
  const edit = new URLSearchParams(location.search).get("edit");
  return (
    <ContactWorkspace
      labels={texts.data}
      locale={locale}
      tenant={tenant}
      user={user}
      id={id}
      edit={edit}
      search={location.search}
      locationKey={location.key}
    />
  );
}
